import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  TrendingUp, Loader2, XCircle, ChevronDown, Sparkles,
  History, ChevronUp, ChevronRight, FileText,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import CompanyPdfUpload from '../components/CompanyPdfUpload';
import { companyPdfErrorKey, uploadCompanyPdf, hasCompleteCompanyHistory } from '../api/companyPdf';
import { formatFinancialValue, isFinancialInputComplete } from '../utils/financialInput';

import Pagination from '../components/Pagination';

/* ─── Types ──────────────────────────────────────────────── */
interface Category { key: string; name: string; }

interface Scenario {
  mrp_value: number;
  amount_mrp?: number;
  purchase_amount?: number;
  tier_max_mrp?: number | null;
  req_pfu?: number;
  user_pd_min?: number;
  user_pun_min?: number;
  user_pfot_min?: number;
  income_3yrs_min: number;
  taxes_3yrs_min: number;
  pfot_3yrs_min: number;
  required_income_target_year?: number | null;
  required_taxes_target_year?: number | null;
  required_pfot_target_year?: number | null;
}

interface PlanningResult {
  category_key: string;
  category_name: string;
  purchase_amount: number;
  years_metadata: { year_1: number; year_2: number; year_3_target: number } | null;
  base_scenario: Scenario;
  mrp_5_scenario: Scenario;
  mrp_10_scenario: Scenario;
  custom_scenario: Scenario;
}

interface TierItem {
  tier_label: string;
  max_mrp: number | null;
  req_pd: number;
  req_pun: number;
  req_pfot: number;
  req_pfu: number;
  base_scenario: Scenario;
  mrp_5_scenario: Scenario;
  mrp_10_scenario: Scenario;
  custom_scenario: Scenario;
}

interface TierResult {
  category_key: string;
  category_name: string;
  base_mrp: number;
  mrp_5: number;
  mrp_10: number;
  custom_mrp: number;
  years_metadata: { year_1: number; year_2: number; year_3_target: number } | null;
  tiers: TierItem[];
}

interface PlanningHistoryItem {
  id: string;
  created_at: string;
  calc_type: 'reverse_planning' | 'tier_requirements';
  category_key: string;
  purchase_amount: number | null;
  result: any;
}

/* ─── Helpers ─────────────────────────────────────────────── */
const fmt = (n: number | null | undefined) =>
  n != null ? new Intl.NumberFormat('ru-RU').format(Math.round(n)) : '—';

const fmtInput = (raw: string): string => {
  let s = raw.replace(/,/g, '.').replace(/[^\d.]/g, '');
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  const [intPart, fracPart] = s.split('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
  if (fracPart !== undefined) return formattedInt + '.' + fracPart.slice(0, 2);
  return formattedInt;
};

const parseNum = (s: string) => parseFloat(s.replace(/[\u00a0\s]/g, '').replace(',', '.')) || 0;

const parseDateStr = (raw: string | null | undefined): Date | null => {
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

const fmtDateTime = (raw: string | null | undefined) => {
  const d = parseDateStr(raw);
  return d ? d.toLocaleString('ru-RU') : '—';
};

type ScenarioKey = 'custom_scenario' | 'mrp_5_scenario' | 'mrp_10_scenario' | 'base_scenario';

/* ─── Scenario Tabs ──────────────────────────────────────── */
function ScenarioTabs({ active, onChange, t }: {
  active: ScenarioKey;
  onChange: (k: ScenarioKey) => void;
  t: (k: string) => string;
}) {
  const tabs: { key: ScenarioKey; label: string; accent: boolean }[] = [
    { key: 'custom_scenario', label: t('fp.scenarioCustom'), accent: true },
    { key: 'mrp_5_scenario', label: t('fp.scenarioMrp5'), accent: true },
    { key: 'mrp_10_scenario', label: t('fp.scenarioMrp10'), accent: true },
    { key: 'base_scenario', label: t('fp.scenarioBase'), accent: false },
  ];
  return (
    <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-none">
      {tabs.map(tab => (
        <button
          key={tab.key}
          type="button"
          onClick={() => onChange(tab.key)}
          className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
            active === tab.key
              ? tab.accent
                ? 'bg-goszakup-600 text-white shadow-sm'
                : 'bg-gray-500 text-white shadow-sm'
              : tab.accent
                ? 'text-goszakup-600 bg-goszakup-50 hover:bg-goszakup-100'
                : 'text-gray-400 bg-gray-100 hover:bg-gray-200'
          }`}
        >
          {tab.label}
          {tab.key === 'base_scenario' && (
            <span className="ml-1 opacity-70 font-normal">{t('fp.scenarioBaseHint')}</span>
          )}
        </button>
      ))}
    </div>
  );
}

/* ─── Scenario Card for Режим А ────────────────────────────── */
function PlanningScenarioCard({ scenario, yearsMetadata, t }: {
  scenario: Scenario;
  yearsMetadata: PlanningResult['years_metadata'];
  t: (k: string) => string;
}) {
  const rows3yrs = [
    { label: t('fp.resultIncome3yrs'), value: scenario.income_3yrs_min },
    { label: t('fp.resultTaxes3yrs'), value: scenario.taxes_3yrs_min },
    { label: t('fp.resultPfot3yrs'), value: scenario.pfot_3yrs_min },
  ];
  const targetRows = [
    { label: t('fp.resultTargetIncome'), value: scenario.required_income_target_year },
    { label: t('fp.resultTargetTaxes'), value: scenario.required_taxes_target_year },
    { label: t('fp.resultTargetPfot'), value: scenario.required_pfot_target_year },
  ];
  const hasTarget = targetRows.some(r => r.value != null);

  return (
    <div className="space-y-4">
      {/* Header metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-goszakup-50 rounded-xl p-3 text-center">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{t('fp.resultReqPfu')}</p>
          <p className="text-2xl font-semibold text-goszakup-700">{scenario.req_pfu ?? '—'}</p>
        </div>
        <div className="bg-slate-50 rounded-xl p-3 text-center">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{t('fp.resultPdMin')}</p>
          <p className="text-2xl font-semibold text-gray-800">{scenario.user_pd_min ?? '—'}</p>
        </div>
        <div className="bg-slate-50 rounded-xl p-3 text-center">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{t('fp.resultPunMin')}</p>
          <p className="text-2xl font-semibold text-gray-800">{scenario.user_pun_min ?? '—'}</p>
        </div>
        <div className="bg-slate-50 rounded-xl p-3 text-center">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{t('fp.resultPfotMin')}</p>
          <p className="text-2xl font-semibold text-gray-800">{scenario.user_pfot_min ?? '—'}</p>
        </div>
      </div>

      {/* МРП meta */}
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full font-medium">
          {t('fp.resultMrpValue')}: <strong>{fmt(scenario.mrp_value)} ₸</strong>
        </span>
        {scenario.amount_mrp != null && (
          <span className="bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full font-medium">
            {t('fp.resultAmountMrp')}: <strong>{fmt(scenario.amount_mrp)} МРП</strong>
          </span>
        )}
      </div>

      {/* Min 3-year requirements */}
      <div>
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{t('fp.resultIncome3yrs').split(' ').slice(0, -1).join(' ')}</p>
        <div className="space-y-2">
          {rows3yrs.map((r, i) => (
            <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-xl p-3">
              <span className="text-sm text-gray-600 flex-1">{r.label}</span>
              <span className="font-semibold text-gray-900">{fmt(r.value)} ₸</span>
            </div>
          ))}
        </div>
      </div>

      {/* Target year checklist */}
      {hasTarget && yearsMetadata && (
        <div className="border border-goszakup-200 rounded-2xl overflow-hidden">
          <div className="bg-goszakup-50 px-4 py-3 border-b border-goszakup-200">
            <p className="text-xs font-bold text-goszakup-700 uppercase tracking-wider">
              {t('fp.resultTargetTitle')} — {yearsMetadata.year_3_target}
            </p>
            <p className="text-xs text-goszakup-600 mt-0.5">
              {t('fp.resultYear1')}: {yearsMetadata.year_1} · {t('fp.resultYear2')}: {yearsMetadata.year_2}
            </p>
          </div>
          <div className="divide-y divide-goszakup-100">
            {targetRows.map((r, i) => (
              r.value != null && (
                <div key={i} className="flex items-center justify-between px-4 py-3">
                  <span className="text-sm text-gray-700">{r.label}</span>
                  <span className="font-semibold text-goszakup-700">{fmt(r.value)} ₸</span>
                </div>
              )
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Tier Accordion Item ────────────────────────────────── */
function TierAccordion({ tier, yearsMetadata, t }: {
  tier: TierItem;
  yearsMetadata: TierResult['years_metadata'];
  t: (k: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const [activeScenario, setActiveScenario] = useState<ScenarioKey>('custom_scenario');
  const scen = tier[activeScenario];

  const targetRows = [
    { label: t('fp.resultTargetIncome'), value: scen.required_income_target_year },
    { label: t('fp.resultTargetTaxes'), value: scen.required_taxes_target_year },
    { label: t('fp.resultTargetPfot'), value: scen.required_pfot_target_year },
  ];
  const hasTarget = yearsMetadata != null && targetRows.some(r => r.value != null);

  return (
    <div className="border border-gray-100 rounded-2xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 bg-white hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-goszakup-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-4 h-4 text-goszakup-600" />
          </div>
          <div className="text-left">
            <p className="font-bold text-gray-900 text-sm">{tier.tier_label}</p>
            <p className="text-xs text-gray-400">{t('fp.tierReqPfu')}: {tier.req_pfu}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex gap-2 text-xs">
            <span className="bg-slate-100 text-gray-600 px-2 py-0.5 rounded-full">{t('fp.tierReqPd')}: {tier.req_pd}</span>
            <span className="bg-slate-100 text-gray-600 px-2 py-0.5 rounded-full">{t('fp.tierReqPun')}: {tier.req_pun}</span>
            <span className="bg-slate-100 text-gray-600 px-2 py-0.5 rounded-full">{t('fp.tierReqPfot')}: {tier.req_pfot}</span>
          </div>
          {open ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
        </div>
      </button>
      {open && (
        <div className="px-5 pb-5 pt-3 space-y-3 bg-white border-t border-gray-100">
          <ScenarioTabs active={activeScenario} onChange={setActiveScenario} t={t} />

          {/* Min 3-year requirements */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { label: t('fp.resultIncome3yrs'), value: scen.income_3yrs_min },
              { label: t('fp.resultTaxes3yrs'), value: scen.taxes_3yrs_min },
              { label: t('fp.resultPfot3yrs'), value: scen.pfot_3yrs_min },
            ].map((r, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{r.label}</p>
                <p className="font-semibold text-gray-900">{fmt(r.value)} ₸</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 text-xs">
            <span className="bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">
              {t('fp.resultMrpValue')}: <strong>{fmt(scen.mrp_value)} ₸</strong>
            </span>
            <span className="bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">
              {t('fp.resultPurchaseAmount')}: <strong>{fmt(scen.purchase_amount)} ₸</strong>
            </span>
          </div>

          {/* Target year checklist */}
          {hasTarget && yearsMetadata && (
            <div className="border border-goszakup-200 rounded-2xl overflow-hidden">
              <div className="bg-goszakup-50 px-4 py-3 border-b border-goszakup-200">
                <p className="text-xs font-bold text-goszakup-700 uppercase tracking-wider">
                  {t('fp.resultTargetTitle')} — {yearsMetadata.year_3_target}
                </p>
                <p className="text-xs text-goszakup-600 mt-0.5">
                  {t('fp.resultYear1')}: {yearsMetadata.year_1} · {t('fp.resultYear2')}: {yearsMetadata.year_2}
                </p>
              </div>
              <div className="divide-y divide-goszakup-100">
                {targetRows.map((r, i) => (
                  r.value != null && (
                    <div key={i} className="flex items-center justify-between px-4 py-3">
                      <span className="text-sm text-gray-700">{r.label}</span>
                      {r.value === 0 ? (
                        <span className="font-semibold text-green-600 flex items-center gap-1">
                          ✓ {t('fp.targetYearDone')}
                        </span>
                      ) : (
                        <span className="font-semibold text-goszakup-700">{fmt(r.value)} ₸</span>
                      )}
                    </div>
                  )
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── History Planning Result ────────────────────────────── */
function HistoryPlanningResult({ result, t }: { result: PlanningResult; t: (k: string) => string }) {
  const [scenKey, setScenKey] = useState<ScenarioKey>('custom_scenario');
  return (
    <div className="space-y-3 border-t border-gray-100 pt-3">
      <ScenarioTabs active={scenKey} onChange={setScenKey} t={t} />
      <PlanningScenarioCard scenario={result[scenKey]} yearsMetadata={result.years_metadata} t={t} />
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────── */
export default function FinancialPlanningPage() {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();

  const [activeTab, setActiveTab] = useState<'planning' | 'history'>('planning');

  // Categories
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const categoryRef = useRef<HTMLDivElement>(null);

  // Mode: false = Режим А (planning), true = Режим Б (tiers)
  const [tierMode, setTierMode] = useState(() => searchParams.get('mode') === 'tier');

  // Form
  const [selectedCategory, setSelectedCategory] = useState('');
  const currentYear = new Date().getFullYear();
  const targetYear = currentYear + 1; // always next year
  const [purchaseAmount, setPurchaseAmount] = useState('');
  const [baseMrp, setBaseMrp] = useState<number | null>(null);
  const [mrp5, setMrp5] = useState<number | null>(null);
  const [mrp10, setMrp10] = useState<number | null>(null);
  const [mrpPercent, setMrpPercent] = useState(0); // 0..100 mapped to 0%..+10%
  const customMrp = baseMrp != null ? +(baseMrp * (1 + (mrpPercent / 100) * 0.1)).toFixed(2) : null;

  // Historical financial fields
  const [incomeY1, setIncomeY1] = useState('');
  const [taxesY1, setTaxesY1] = useState('');
  const [pfotY1, setPfotY1] = useState('');
  const [incomeY2, setIncomeY2] = useState('');
  const [taxesY2, setTaxesY2] = useState('');
  const [pfotY2, setPfotY2] = useState('');
  const [companyApplied, setCompanyApplied] = useState(false);

  // Results
  const [planningResult, setPlanningResult] = useState<PlanningResult | null>(null);
  const [tierResult, setTierResult] = useState<TierResult | null>(null);
  const [activeScenario, setActiveScenario] = useState<ScenarioKey>('custom_scenario');
  const [isCalculating, setIsCalculating] = useState(false);
  const [calcError, setCalcError] = useState('');

  // History
  const [historyItems, setHistoryItems] = useState<PlanningHistoryItem[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyFilter, setHistoryFilter] = useState<'all' | 'reverse_planning' | 'tier_requirements'>('all');
  const [selectedHistory, setSelectedHistory] = useState<PlanningHistoryItem | null>(null);

  // No-company modal
  const [showNoCompanyModal, setShowNoCompanyModal] = useState(false);

  // Confirm modal
  const [showConfirm, setShowConfirm] = useState(false);

  // PDF upload
  const [isPdfUploading, setIsPdfUploading] = useState(false);
  const [pdfStatus, setPdfStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [pdfOpen, setPdfOpen] = useState(false);

  /* ─── Effects ─────────────────────────────────────────── */
  useEffect(() => {
    setTierMode(searchParams.get('mode') === 'tier');
    setActiveTab('planning'); setPlanningResult(null); setTierResult(null); setCalcError('');
  }, [searchParams]);
  useEffect(() => {
    api.get('/pfu/categories')
      .then(res => setCategories(res.data))
      .catch(() => {})
      .finally(() => setCategoriesLoading(false));
    api.get('/pfu/mrp').then(res => {
      setBaseMrp(res.data.mrp_value);
      setMrp5(res.data.mrp_5);
      setMrp10(res.data.mrp_10);
    }).catch(() => {});
  }, []);

  const fetchHistory = useCallback(async (page: number, filter = historyFilter) => {
    setIsHistoryLoading(true); setHistoryError('');
    try {
      const params: Record<string, any> = { page, limit: 10 };
      if (filter !== 'all') params.calc_type = filter;
      const res = await api.get('/users/me/planning-history', { params });
      const data = res.data;
      setHistoryItems(data.items ?? []);
      setHistoryTotalPages(data.total_pages ?? 1);
      setHistoryPage(data.page ?? page);
    } catch { setHistoryError(t('fp.errorDefault')); }
    finally { setIsHistoryLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyFilter]);

  useEffect(() => {
    if (activeTab === 'history') fetchHistory(1, historyFilter);
  }, [activeTab, historyFilter]); // eslint-disable-line

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) setCategoryOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ─── Handlers ─────────────────────────────────────────── */
  const applyCompanyData = () => {
    if (!user?.company_profile) { setShowNoCompanyModal(true); return; }
    const cp = user.company_profile;
    setIncomeY1(formatFinancialValue(cp.history_income_year_1));
    setIncomeY2(formatFinancialValue(cp.history_income_year_2));
    setTaxesY1(formatFinancialValue(cp.history_taxes_year_1));
    setTaxesY2(formatFinancialValue(cp.history_taxes_year_2));
    setPfotY1(formatFinancialValue(cp.history_pfot_year_1));
    setPfotY2(formatFinancialValue(cp.history_pfot_year_2));
    setCompanyApplied(true);
  };

  const handlePdfFile = async (file: File) => {
    if (isPdfUploading || isCalculating) return;
    if (!file.name.toLowerCase().endsWith('.pdf') || file.size > 5 * 1024 * 1024) {
      setPdfStatus({ type: 'error', msg: t('fp.pdfError400') }); return;
    }
    setIsPdfUploading(true); setPdfStatus(null);
    try {
      const data = await uploadCompanyPdf(file);
      const {
        history_income_year_1, history_income_year_2,
        history_taxes_year_1, history_taxes_year_2,
        history_pfot_year_1, history_pfot_year_2,
      } = data;
      const fv = formatFinancialValue;
      setIncomeY1(fv(history_income_year_1)); setIncomeY2(fv(history_income_year_2));
      setTaxesY1(fv(history_taxes_year_1)); setTaxesY2(fv(history_taxes_year_2));
      setPfotY1(fv(history_pfot_year_1)); setPfotY2(fv(history_pfot_year_2));
      setCompanyApplied(true);
      const completeHistory = hasCompleteCompanyHistory(data);
      setPdfStatus({ type: 'success', msg: t(completeHistory ? 'fp.pdfSuccess' : 'pdfImport.incompleteHistory') });
      await refreshUser();
      if (completeHistory) setTimeout(() => setShowNoCompanyModal(false), 1500);
    } catch (err: any) {
      setPdfStatus({ type: 'error', msg: t(companyPdfErrorKey(err, 'fp')) });
    } finally { setIsPdfUploading(false); }
  };

  const calculate = async () => {
    if (isCalculating || isPdfUploading || ![incomeY1, incomeY2, taxesY1, taxesY2, pfotY1, pfotY2].every(isFinancialInputComplete)) return;
    setIsCalculating(true); setCalcError(''); setPlanningResult(null); setTierResult(null);
    try {
      if (tierMode) {
        const body: Record<string, any> = {
          category_key: selectedCategory,
          target_tender_year: targetYear,
          history_income_year_1: parseNum(incomeY1),
          history_income_year_2: parseNum(incomeY2),
          history_taxes_year_1: parseNum(taxesY1),
          history_taxes_year_2: parseNum(taxesY2),
          history_pfot_year_1: parseNum(pfotY1),
          history_pfot_year_2: parseNum(pfotY2),
        };
        if (customMrp != null) body.custom_mrp = customMrp;
        const res = await api.post('/pfu/tier-requirements', body);
        setTierResult(res.data);
        if (!res.data.tiers?.length) setCalcError(t('fp.errorDefault'));
      } else {
        const body: Record<string, any> = {
          category_key: selectedCategory,
          purchase_amount: parseNum(purchaseAmount),
          target_tender_year: targetYear,
          history_income_year_1: parseNum(incomeY1),
          history_income_year_2: parseNum(incomeY2),
          history_taxes_year_1: parseNum(taxesY1),
          history_taxes_year_2: parseNum(taxesY2),
          history_pfot_year_1: parseNum(pfotY1),
          history_pfot_year_2: parseNum(pfotY2),
        };
        if (customMrp != null) body.custom_mrp = customMrp;
        const res = await api.post('/pfu/reverse-planning', body);
        setPlanningResult(res.data);
        setActiveScenario('custom_scenario');
      }
    } catch (err: any) {
      const s = err.response?.status;
      setCalcError(
        s === 402 ? t('fp.error402') :
        s === 404 ? t('fp.error404') :
        s === 422 ? t('fp.error422') :
        t('fp.errorDefault')
      );
    } finally { setIsCalculating(false); }
    await refreshUser();
  };

  const selectedCategoryName = categories.find(c => c.key === selectedCategory)?.name ?? '';
  const planningLeft = user?.balance?.planning_calculations_left ?? 0;
  const tierLeft = user?.balance?.tier_requirements_calculations_left ?? 0;
  const limitsLeft = tierMode ? tierLeft : planningLeft;
  const historyValues = [incomeY1, incomeY2, taxesY1, taxesY2, pfotY1, pfotY2];
  const completeHistory = historyValues.every(isFinancialInputComplete);
  const canCalculate = !!(completeHistory && selectedCategory && (tierMode || parseNum(purchaseAmount) > 0) && limitsLeft > 0);

  /* ─── Render ──────────────────────────────────────────── */
  return (
    <div className="workspace-page workspace-form-width">
        <header className="workspace-header"><div>
          <h1 className="workspace-title">{t('nav.financialPlanning')}</h1>
          <p className="workspace-subtitle">{language === 'kk' ? 'Өткен жылдардың деректерін ескеріп, мақсатты сатып алу сомасына қажетті табыс, салықтар мен еңбекақы қорын есептеңіз.' : 'Узнайте, какие доходы, налоги и фонд оплаты труда нужны для целевой суммы закупки с учётом данных прошлых лет.'}</p>
        </div></header>
        <div className="space-y-6">

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl p-1 border border-gray-100 shadow-sm">
          {(['planning', 'history'] as const).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-bold transition-all ${activeTab === tab ? 'bg-goszakup-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {tab === 'planning' ? t('fp.tabPlanning') : <span className="flex items-center justify-center gap-1.5"><History className="w-4 h-4" />{t('fp.tabHistory')}</span>}
            </button>
          ))}
        </div>

        {/* ── PLANNING TAB ── */}
        {activeTab === 'planning' && (
          <>
            {/* Company data */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                <h2 className="font-semibold text-gray-900">{t('fp.companyDataTitle')}</h2>
                <div className="flex flex-col items-stretch sm:items-end gap-2">
                  <button onClick={applyCompanyData}
                    className={`flex items-center justify-center gap-2 text-sm font-bold px-4 py-2 rounded-xl border transition-all ${companyApplied ? 'bg-green-50 text-green-700 border-green-200' : 'bg-goszakup-50 text-goszakup-700 border-sky-200 hover:bg-goszakup-100'}`}
                  >
                    <Sparkles className="w-4 h-4" />
                    {companyApplied ? t('fp.useCompanyApplied') : t('fp.useCompanyBtn')}
                  </button>
                  <button
                    type="button"
                    aria-expanded={pdfOpen}
                    onClick={() => { setPdfOpen(o => !o); setPdfStatus(null); }}
                    className={`flex items-center justify-center gap-2 text-sm font-bold min-h-11 px-4 py-2 rounded-xl border transition-all ${pdfOpen ? 'bg-gray-100 text-gray-700 border-gray-200' : 'bg-goszakup-600 text-white border-goszakup-600 hover:bg-goszakup-700'}`}
                  >
                    <FileText className="w-3.5 h-3.5" />
                    {pdfOpen ? t('fp.pdfHide') : t('fp.pdfLabel')}
                  </button>
                </div>
              </div>

              {/* PDF import — toggled */}
              {pdfOpen && (
                <div className="mb-4">
                  <CompanyPdfUpload inputLabel={t('fp.pdfLabel')} hint={t('fp.pdfHint')}
                    busy={isPdfUploading} onFile={handlePdfFile} />
                  {pdfStatus && (
                    <p className={`text-xs mt-1 font-medium ${pdfStatus.type === 'success' ? 'text-green-600' : 'text-red-500'}`}>{pdfStatus.msg}</p>
                  )}
                </div>
              )}

              {/* Historical fields */}
              {(
                <div className="space-y-4">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('fp.historyDataTitle')}</p>
                  {!completeHistory && (companyApplied || historyValues.some(value => value.trim() !== '')) && (
                    <p role="status" className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm leading-relaxed text-amber-900">{t('pdfImport.historyRequired')}</p>
                  )}
                  {(() => {
                    const y1 = targetYear - 4;
                    const y2 = targetYear - 3;
                    const rows = [
                      {
                        fields: [
                          { label: `${t('fp.historyIncomeLabelBase')} ${y1} ${t('fp.historyYearWord')}`, val: incomeY1, set: setIncomeY1 },
                          { label: `${t('fp.historyIncomeLabelBase')} ${y2} ${t('fp.historyYearWord')}`, val: incomeY2, set: setIncomeY2 },
                        ],
                      },
                      {
                        fields: [
                          { label: `${t('fp.historyTaxesLabelBase')} ${y1} ${t('fp.historyYearWord')}`, val: taxesY1, set: setTaxesY1 },
                          { label: `${t('fp.historyTaxesLabelBase')} ${y2} ${t('fp.historyYearWord')}`, val: taxesY2, set: setTaxesY2 },
                        ],
                      },
                      {
                        fields: [
                          { label: `${t('fp.historyPfotLabelBase')} ${y1} ${t('fp.historyYearWord')}`, val: pfotY1, set: setPfotY1 },
                          { label: `${t('fp.historyPfotLabelBase')} ${y2} ${t('fp.historyYearWord')}`, val: pfotY2, set: setPfotY2 },
                        ],
                      },
                    ];
                    return rows.map((row, ri) => (
                      <div key={ri} className="grid grid-cols-1 sm:grid-cols-2 sm:items-end gap-3">
                        {row.fields.map((f, fi) => (
                          <div key={fi}>
                            <label className="block text-xs font-semibold text-gray-500 leading-snug mb-1.5">{f.label} (₸)</label>
                            <div className="relative">
                              <input type="text" inputMode="decimal" aria-label={`${f.label} (₸)`} value={f.val} placeholder="0"
                                onChange={e => { f.set(fmtInput(e.target.value)); setCompanyApplied(false); }}
                                className="w-full px-4 py-3 pr-8 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-goszakup-500 transition-all"
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-bold">₸</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>

            {/* Tier mode banner */}
            <div className={`rounded-xl p-5 border transition-colors ${tierMode ? 'bg-goszakup-50 border-goszakup-200' : 'bg-white border-gray-100 shadow-sm'}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${tierMode ? 'bg-white' : 'bg-goszakup-50'}`}>
                    <FileText className={`w-6 h-6 ${tierMode ? 'text-goszakup-800' : 'text-goszakup-700'}`} />
                  </div>
                  <div>
                    <p className={`font-semibold ${tierMode ? 'text-goszakup-900' : 'text-gray-900'}`}>{t('fp.tierBannerTitle')}</p>
                    <p className={`text-xs mt-0.5 ${tierMode ? 'text-goszakup-800' : 'text-gray-500'}`}>{t('fp.tierBannerDesc')}</p>
                  </div>
                </div>
                <button
                  onClick={() => { setTierMode(v => !v); setPlanningResult(null); setTierResult(null); setCalcError(''); }}
                  className={`flex-shrink-0 text-xs font-bold px-4 py-2.5 rounded-xl transition-all ${tierMode ? 'border border-goszakup-300 bg-white text-goszakup-800 hover:bg-goszakup-100' : 'border border-slate-300 text-slate-700 hover:bg-slate-50'}`}
                >
                  {tierMode ? t('fp.tierBannerBtnBack') : t('fp.tierBannerBtn')}
                </button>
              </div>
            </div>

            {/* Form card */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6 space-y-5">

              {/* Category */}
              <div ref={categoryRef}>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{t('fp.categoryLabel')}</label>
                {categoriesLoading ? (
                  <div className="h-12 bg-gray-100 rounded-xl animate-pulse" />
                ) : (
                  <div className="relative">
                    <button onClick={() => setCategoryOpen(o => !o)} type="button"
                      className={`w-full flex items-center justify-between px-4 py-3 border rounded-xl text-sm transition-all text-left ${categoryOpen ? 'border-goszakup-500 ring-2 ring-goszakup-500/20' : 'border-gray-200 hover:border-gray-300'}`}
                    >
                      <span className={selectedCategory ? 'text-gray-900 font-medium' : 'text-gray-400'}>
                        {selectedCategoryName || t('fp.categoryPlaceholder')}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${categoryOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {categoryOpen && (
                      <div className="absolute z-20 w-full mt-1 bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden">
                        <div className="max-h-56 overflow-y-auto">
                          {categories.map(cat => (
                            <button key={cat.key} type="button" onClick={() => { setSelectedCategory(cat.key); setCategoryOpen(false); }}
                              className={`w-full text-left px-4 py-3 text-sm hover:bg-goszakup-50 transition-colors ${selectedCategory === cat.key ? 'bg-goszakup-50 text-goszakup-700 font-bold' : 'text-gray-700'}`}
                            >
                              {cat.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* MRP slider */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('fp.mrpLabel')}</label>
                  {baseMrp != null && (
                    <span className="text-sm font-semibold text-goszakup-700 bg-goszakup-50 px-3 py-1 rounded-xl">
                      {customMrp != null ? fmt(customMrp) : fmt(baseMrp)} ₸
                      {mrpPercent > 0 && <span className="text-xs font-normal ml-1 text-goszakup-500">(+{(mrpPercent * 0.1).toFixed(1)}%)</span>}
                    </span>
                  )}
                </div>
                <input
                  type="range" min={0} max={100} step={1}
                  value={mrpPercent}
                  onChange={e => setMrpPercent(Number(e.target.value))}
                  className="w-full h-2 cursor-pointer accent-goszakup-600 mb-1"
                />
                <div className="flex justify-between text-xs text-gray-400">
                  <span>{t('fp.mrpBase')}: {baseMrp != null ? fmt(baseMrp) : '…'} ₸</span>
                  <span>+5%: {mrp5 != null ? fmt(mrp5) : '…'} ₸</span>
                  <span>+10%: {mrp10 != null ? fmt(mrp10) : '…'} ₸</span>
                </div>
              </div>

              {/* Purchase amount (mode A only) */}
              {!tierMode && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{t('fp.amountLabel')}</label>
                  <div className="relative mb-3">
                    <input
                      type="text" inputMode="decimal" aria-label={t('fp.amountLabel')} value={purchaseAmount} placeholder="0"
                      onChange={e => setPurchaseAmount(fmtInput(e.target.value))}
                      onPaste={e => { e.preventDefault(); setPurchaseAmount(fmtInput(e.clipboardData.getData('text'))); }}
                      className="w-full px-4 py-4 pr-10 text-2xl font-bold border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-goszakup-500 transition-all"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl font-bold">₸</span>
                  </div>
                  <input type="range" min={0} max={1000000000} step={1000000} value={parseNum(purchaseAmount)}
                    onChange={e => setPurchaseAmount(fmtInput(e.target.value))}
                    className="w-full h-2 cursor-pointer accent-goszakup-600 mb-1"
                  />
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>0 ₸</span><span>1 000 000 000 ₸</span>
                  </div>
                </div>
              )}
            </div>

            {/* Calculate button */}
            <button onClick={() => setShowConfirm(true)} disabled={!canCalculate || isCalculating || isPdfUploading}
              className="w-full min-h-12 py-3 px-6 rounded-lg font-semibold text-sm sm:text-base transition-colors flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed bg-goszakup-600 text-white hover:bg-goszakup-700 shadow-goszakup-600/20"
            >
              {isCalculating
                ? <><Loader2 className="w-5 h-5 animate-spin" />{tierMode ? t('fp.tierCalcRunning') : t('fp.analyzing')}</>
                : <><TrendingUp className="w-5 h-5" />{tierMode ? t('fp.btnTier') : t('fp.btnCalculate')}</>
              }
            </button>

            {/* Limit counter */}
            {user !== null && (
              <div className={`flex flex-wrap items-center justify-between gap-2 text-sm px-1 ${limitsLeft === 0 ? 'text-red-700' : 'text-slate-600'}`}>
                <span>{tierMode ? t('fp.limitTier') : t('fp.limitPlanning')}: <strong>{limitsLeft}</strong></span>
                {limitsLeft === 0 && (
                  <a href="/profile#billing" className="font-bold underline text-red-600 hover:text-red-700">{t('fp.limitBuyLink')}</a>
                )}
              </div>
            )}

            {/* Error */}
            {calcError && (
              <div className="bg-red-50 border border-red-100 rounded-2xl p-4 flex items-center gap-3 text-red-600 text-sm font-medium">
                <XCircle className="w-5 h-5 flex-shrink-0" />{calcError}
              </div>
            )}

            {/* ── RESULT MODE A: Planning ── */}
            {planningResult && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200 bg-slate-50 text-slate-900">
                  <p className="text-lg font-semibold">{t('fp.resultTitle')}</p>
                  <p className="text-sm opacity-80">{planningResult.category_name} · {fmt(planningResult.purchase_amount)} ₸</p>
                </div>
                <div className="p-5 space-y-4">
                  <ScenarioTabs active={activeScenario} onChange={setActiveScenario} t={t} />
                  <PlanningScenarioCard
                    scenario={planningResult[activeScenario]}
                    yearsMetadata={planningResult.years_metadata}
                    t={t}
                  />
                </div>
              </div>
            )}

            {/* ── RESULT MODE B: Tiers ── */}
            {tierResult && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200 bg-slate-50 text-slate-900">
                  <p className="text-lg font-semibold">{t('fp.tierBannerTitle')}</p>
                  <p className="text-sm opacity-80">{tierResult.category_name}</p>
                  <div className="flex flex-wrap gap-2 mt-2 text-xs">
                    <span className="bg-white border border-slate-200 px-2 py-0.5 rounded-md text-slate-600">{t('fp.mrpBase')}: {fmt(tierResult.base_mrp)} ₸</span>
                    <span className="bg-white border border-slate-200 px-2 py-0.5 rounded-md text-slate-600">+5%: {fmt(tierResult.mrp_5)} ₸</span>
                    <span className="bg-white border border-slate-200 px-2 py-0.5 rounded-md text-slate-600">+10%: {fmt(tierResult.mrp_10)} ₸</span>
                    {tierResult.custom_mrp !== tierResult.base_mrp && (
                      <span className="bg-white border border-slate-200 px-2 py-0.5 rounded-md text-slate-600">{t('fp.mrpCustom')}: {fmt(tierResult.custom_mrp)} ₸</span>
                    )}
                  </div>
                </div>
                <div className="p-5 space-y-3">
                  {tierResult.tiers.map((tier, i) => (
                    <TierAccordion key={i} tier={tier} yearsMetadata={tierResult.years_metadata} t={t} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* ── HISTORY TAB ── */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            {/* Filter */}
            <div className="flex gap-1 bg-white rounded-xl p-1 border border-gray-100 shadow-sm">
              {(['all', 'reverse_planning', 'tier_requirements'] as const).map(f => (
                <button key={f} onClick={() => { setHistoryFilter(f); fetchHistory(1, f); }}
                  className={`flex-1 py-2 px-2 rounded-lg text-xs font-bold transition-all ${historyFilter === f ? 'bg-goszakup-600 text-white' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  {f === 'all' ? '—' : f === 'reverse_planning' ? t('fp.historyTypePlanning') : t('fp.historyTypeTier')}
                </button>
              ))}
            </div>

            {isHistoryLoading ? (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 flex justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-goszakup-500" />
              </div>
            ) : historyError ? (
              <div className="bg-red-50 border border-red-100 rounded-2xl p-4 text-red-600 text-sm">{historyError}</div>
            ) : historyItems.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <History className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-lg font-semibold text-gray-900 mb-2">{t('fp.historyEmpty')}</p>
                <p className="text-sm text-gray-400">{t('fp.historyEmptyDesc')}</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-gray-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">{t('fp.historyDate')}</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">{t('fp.historyCategory')}</th>
                        <th className="px-4 py-3 text-right text-xs font-bold text-gray-400 uppercase tracking-wider">{t('fp.historyAmount')}</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-gray-400 uppercase tracking-wider">{t('fp.historyType')}</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-gray-400 uppercase tracking-wider"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {historyItems.map(item => (
                        <tr key={item.id} onClick={() => setSelectedHistory(item)}
                          className="hover:bg-slate-50 cursor-pointer transition-colors"
                        >
                          <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{fmtDateTime(item.created_at)}</td>
                          <td className="px-4 py-3 font-medium text-gray-900">{item.result?.category_name ?? item.category_key}</td>
                          <td className="px-4 py-3 text-right font-medium text-gray-900">
                            {item.purchase_amount != null ? `${fmt(item.purchase_amount)} ₸` : '—'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${item.calc_type === 'reverse_planning' ? 'bg-goszakup-100 text-goszakup-700' : 'bg-amber-100 text-amber-700'}`}>
                              {item.calc_type === 'reverse_planning' ? t('fp.historyTypePlanning') : t('fp.historyTypeTier')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <ChevronRight className="w-4 h-4 text-gray-300 inline-block" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {historyTotalPages > 1 && (
                  <Pagination currentPage={historyPage} totalPages={historyTotalPages} onPageChange={p => fetchHistory(p)} />
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── HISTORY DETAIL MODAL ── */}
      {selectedHistory && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" onClick={() => setSelectedHistory(null)}>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{t('fp.historyDetailTitle')}</h3>
              <button onClick={() => setSelectedHistory(null)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                <XCircle className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{t('fp.historyDate')}</p>
                  <p className="font-bold text-gray-900 text-sm">{fmtDateTime(selectedHistory.created_at)}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{t('fp.historyType')}</p>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${selectedHistory.calc_type === 'reverse_planning' ? 'bg-goszakup-100 text-goszakup-700' : 'bg-amber-100 text-amber-700'}`}>
                    {selectedHistory.calc_type === 'reverse_planning' ? t('fp.historyTypePlanning') : t('fp.historyTypeTier')}
                  </span>
                </div>
                <div className="bg-slate-50 rounded-xl p-3 col-span-2">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{t('fp.historyCategory')}</p>
                  <p className="font-bold text-gray-900 text-sm">{selectedHistory.result?.category_name ?? selectedHistory.category_key}</p>
                </div>
                {selectedHistory.purchase_amount != null && (
                  <div className="bg-slate-50 rounded-xl p-3 col-span-2">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{t('fp.historyAmount')}</p>
                    <p className="font-bold text-gray-900 text-sm">{fmt(selectedHistory.purchase_amount)} ₸</p>
                  </div>
                )}
              </div>

              {/* Inline result */}
              {selectedHistory.result && selectedHistory.calc_type === 'reverse_planning' && (
                <HistoryPlanningResult result={selectedHistory.result as PlanningResult} t={t} />
              )}

              {selectedHistory.result && selectedHistory.calc_type === 'tier_requirements' && (() => {
                const r = selectedHistory.result as TierResult;
                return (
                  <div className="space-y-2 border-t border-gray-100 pt-3">
                    {(r.tiers ?? []).map((tier, i) => (
                      <TierAccordion key={i} tier={tier} yearsMetadata={r.years_metadata ?? null} t={t} />
                    ))}
                  </div>
                );
              })()}
            </div>
            <div className="px-6 py-4 border-t border-gray-100">
              <button onClick={() => setSelectedHistory(null)}
                className="w-full py-3 text-sm font-bold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >{t('fp.historyDetailClose')}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Confirm calculation modal */}
      {showConfirm && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-xl p-6 max-w-sm w-full space-y-4">
            <p className="font-semibold text-gray-900 text-lg">{t('fp.confirmTitle')}</p>
            <p className="text-sm text-gray-500">{tierMode ? t('fp.confirmTierDesc') : t('fp.confirmPlanDesc')}</p>
            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-bold hover:bg-gray-200 transition-colors"
              >{t('fp.confirmCancel')}</button>
              <button
                onClick={() => { setShowConfirm(false); calculate(); }}
                className="flex-1 py-2.5 rounded-xl bg-goszakup-600 text-white text-sm font-bold hover:bg-goszakup-700 transition-colors shadow-sm"
              >{t('fp.confirmBtn')}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* No company modal */}
      {showNoCompanyModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-xl p-6 max-w-sm w-full space-y-4">
            <p className="font-bold text-gray-800">{t('fp.noCompanyTitle')}</p>
            <p className="text-sm text-gray-500">{t('fp.noCompanyDesc')}</p>
            <button onClick={() => setShowNoCompanyModal(false)}
              className="w-full py-2.5 rounded-xl bg-goszakup-600 text-white text-sm font-bold hover:bg-goszakup-700 transition-colors"
            >{t('fp.noCompanyClose')}</button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
